/**
 * „Nagrania" (canon FV6-01 and FV6-02): every recording of the client, newest first, by month, read a
 * page at a time with `ClipOlderPages` at the end; narrowed to one movement, that movement's own.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ClipOlderPages, ClipTileGrid, ClipTileRowSkeleton } from '~/entities/exercise-clip';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { VideoMarkIcon } from '~/shared/ui/EmptyStateIcons';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { FormaGalleryState } from '../lib/useFormaGallery';

import forma from './Forma.module.css';

const SKELETON_TILES = 5;

interface FormaGalleryProps {
  readonly gallery: FormaGalleryState;
  readonly gate: SkeletonGate;
  /** Narrowed to one movement: its empty state speaks of that movement. */
  readonly narrowed: boolean;
  readonly onOpenClip: (clipId: number) => void;
}

export function FormaGallery({ gallery, gate, narrowed, onOpenClip }: FormaGalleryProps) {
  const { t } = useTranslation();
  const region = useRef<HTMLElement>(null);
  const { view } = gallery;
  const retry = () => {
    gallery.retry();
    region.current?.focus();
  };
  const retryButton = (
    <button className={inlineButton.inlineTextButton} onClick={retry} type="button">
      {t('web:clientDetail.states.retry')}
    </button>
  );

  return (
    <section
      aria-busy={gate.waiting}
      aria-label={t('web:forma.gallery.label')}
      className={forma.galleryCard}
      ref={region}
      tabIndex={-1}
    >
      {gate.waiting ? (
        <>
          <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
          {gate.skeleton ? <ClipTileRowSkeleton drawn month tiles={SKELETON_TILES} /> : null}
        </>
      ) : (
        <div className={`${String(forma.galleryBody)} ${gate.reveal}`}>
          {gallery.failed ? (
            <p className={forma.listNotice} role="alert">
              {t('web:forma.gallery.failed')} {retryButton}
            </p>
          ) : (
            <>
              {view.missing === null ? null : (
                <p className={forma.listNotice} role="alert">
                  {view.missing} {retryButton}
                </p>
              )}
              {view.count === 0 && view.missing === null ? (
                <div className={forma.galleryEmpty}>
                  <EmptyState
                    mark={<VideoMarkIcon />}
                    tone="neutral"
                    {...(narrowed
                      ? { title: t('web:forma.dock.empty') }
                      : { title: t('web:forma.gallery.empty'), hint: t('web:forma.gallery.emptyHint') })}
                  />
                </div>
              ) : (
                <>
                  <ClipTileGrid months={view.months} onOpenClip={onOpenClip} />
                  {gallery.older === null ? null : <ClipOlderPages older={gallery.older} />}
                </>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
